/**
 * Pure presentation of the monthly GAM refresh on the benchmark tabs: where
 * each row's numbers came from, the "based on" sample behind a computed row,
 * the refresh banner and the email's data-window lines.
 *
 * The refresh (backend adBenchmarksRefreshService) recomputes both sets from
 * the last 3 full months of GAM delivery: benchmark = the typical (median)
 * campaign, range = the middle 50% of campaigns, counting campaigns with
 * 10,000+ impressions and only rows with 3+ such campaigns. It replaces the
 * numbers on those rows — hand edits included — and keeps labels and notes.
 */

import type { StatusVariant } from '../../ui';
import type {
  AdBenchmarksRefreshStatus,
  AdUnitBenchmarkKey,
  AdUnitBenchmarkValues,
  BenchmarkMetricStats,
  BenchmarkWindow,
  ComputedBenchmarkBlock,
  ComputedBenchmarkRow,
} from '../../../services/adBenchmarksApi';

/** Where a row's numbers come from. 'manual' = no GAM computation yet (or not for this row). */
export type RowSource = 'gam' | 'edited' | 'not-enough-data' | 'placeholder' | 'manual';

const NUMBER_FIELDS = ['ctr', 'ctrRange', 'vcr', 'vcrRange'] as const;

export function rowSource(
  computed: ComputedBenchmarkBlock | null,
  key: AdUnitBenchmarkKey,
  row: AdUnitBenchmarkValues | undefined,
  isPlaceholder: boolean,
): RowSource {
  if (isPlaceholder) return 'placeholder';
  const computedRow = computed?.rows[key];
  if (!computedRow || !row) return 'manual';
  if (!computedRow.values) return 'not-enough-data';
  const values = computedRow.values;
  return NUMBER_FIELDS.every((field) => (row[field] ?? '').trim() === values[field]) ? 'gam' : 'edited';
}

/** Pill per source; placeholder and manual rows have their own (or no) marker. */
export const ROW_SOURCE_PILL: Record<'gam' | 'edited' | 'not-enough-data', { variant: StatusVariant; label: string; title: string }> = {
  gam: {
    variant: 'ok',
    label: 'From GAM',
    title: 'These numbers come from the last GAM refresh.',
  },
  edited: {
    variant: 'pending',
    label: 'Edited — replaced at next refresh',
    title: 'Edited by hand since the last GAM refresh. The next monthly refresh replaces these numbers.',
  },
  'not-enough-data': {
    variant: 'draft',
    label: 'Not enough data — kept',
    title:
      'Fewer than 3 campaigns with 10,000+ impressions in the last refresh, so this row kept its numbers. ' +
      'Hand edits here stay until a refresh has enough data.',
  },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function ymdParts(ymd: string): { year: number; month: number; day: number } | null {
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) } : null;
}

/** '2026-11-02' → 'Nov 2' (or 'Nov 2, 2026'). */
export function formatYmd(ymd: string, withYear = false): string {
  const p = ymdParts(ymd);
  if (!p) return ymd;
  return `${MONTHS[p.month - 1]} ${p.day}${withYear ? `, ${p.year}` : ''}`;
}

/** 'Jul 1 – Sep 30, 2026', or 'Nov 1, 2026 – Jan 31, 2027' across a year. */
export function formatWindow(window: BenchmarkWindow): string {
  const start = ymdParts(window.startYmd);
  const end = ymdParts(window.endYmd);
  if (!start || !end) return `${window.startYmd} – ${window.endYmd}`;
  return start.year === end.year
    ? `${formatYmd(window.startYmd)} – ${formatYmd(window.endYmd, true)}`
    : `${formatYmd(window.startYmd, true)} – ${formatYmd(window.endYmd, true)}`;
}

/** An ISO instant → its LA calendar date, 'Oct 2'. */
export function formatInstant(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' });
}

/** 23_400_000 → '23.4M', 812_345 → '812K', 9_500 → '9,500'. */
export function compactCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString('en-US');
}

const pct = (fraction: number | null, digits: number): string => (fraction == null ? '—' : `${(fraction * 100).toFixed(digits)}%`);

function metricLine(name: string, stats: BenchmarkMetricStats, digits: number): string {
  return (
    `${name}: median ${pct(stats.median, digits)} · middle 50% ${pct(stats.p25, digits)} – ${pct(stats.p75, digits)} · ` +
    `pooled ${pct(stats.pooled, digits)} · ${stats.eligible} of ${stats.campaigns} campaigns with 10,000+ impressions`
  );
}

/**
 * The "Based on" cell for a computed row: the weakest metric's campaign count
 * (a row needs 3+ for every metric it shows) and the row's impressions, with
 * the full statistics in the tooltip. Internal only — never copied to email.
 */
export function sampleSummary(row: ComputedBenchmarkRow): { label: string; title: string } {
  const metrics = [row.ctr, row.vcr].filter((m): m is BenchmarkMetricStats => m != null);
  const campaigns = Math.min(...metrics.map((m) => m.eligible));
  const lines = [
    row.ctr ? metricLine('CTR', row.ctr, 3) : 'CTR: no click benchmark for CTV',
    metricLine('Completion rate', row.vcr, 1),
    `${row.orders} campaigns · ${row.lineItems} line items · ${row.impressions.toLocaleString('en-US')} impressions in this row`,
  ];
  return {
    label: `${campaigns} campaign${campaigns === 1 ? '' : 's'} · ${compactCount(row.impressions)} impr.`,
    title: lines.join('\n'),
  };
}

export type BannerTone = 'neutral' | 'running' | 'failed';

/** The refresh banner above the table. `computed` = this tab's last computation. */
export function refreshBanner(
  status: AdBenchmarksRefreshStatus,
  computed: ComputedBenchmarkBlock | null,
): { tone: BannerTone; text: string } {
  if (status.state === 'running') {
    const window = status.lastRun?.window ?? status.window;
    return {
      tone: 'running',
      text:
        `Recomputing from GAM — campaigns delivered ${formatWindow(window)}. ` +
        'This takes a few minutes; you can leave this page.',
    };
  }
  if (status.state === 'failed') {
    const why = status.lastRun?.error ? ` ${status.lastRun.error}` : '';
    return { tone: 'failed', text: `The last GAM refresh failed — no benchmarks were changed.${why}` };
  }
  if (status.awaitingFirstPublish || !status.lastSuccess) {
    return {
      tone: 'neutral',
      text:
        'Not computed from GAM yet — these numbers were entered by hand. Recompute now replaces them with the ' +
        `typical campaign from ${formatWindow(status.window)}; after that they refresh automatically each month.`,
    };
  }
  const parts = [
    `Refreshed from GAM ${formatInstant(computed?.computedAt ?? status.lastSuccess.at)}`,
    ...(computed ? [`campaigns delivered ${formatWindow(computed.window)}`] : []),
    status.enabled && status.nextScheduledYmd ? `next refresh ${formatYmd(status.nextScheduledYmd)}` : 'automatic refresh paused',
  ];
  return { tone: 'neutral', text: parts.join(' · ') };
}

/**
 * The email's data-window line and method note — client-facing claims, so
 * only when EVERY copied row's numbers come from that GAM computation (an
 * edited, kept, hand-entered or placeholder row would make them untrue).
 */
export function emailContext(
  computed: ComputedBenchmarkBlock | null,
  sources: RowSource[],
): { subtitle?: string; footnote?: string } {
  if (!computed || sources.length === 0 || !sources.every((source) => source === 'gam')) return {};
  return {
    subtitle: `Based on Culture Genesis campaigns delivered ${formatWindow(computed.window)}`,
    footnote: 'Benchmark = the typical (median) campaign; range = the middle 50% of campaigns.',
  };
}
